import React, { useState, useEffect } from 'react';
import { storage, textdb } from '../../firebaseConfig';
import { doc, setDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate } from 'react-router-dom';
import styles from './ImageUpload.module.css'; // Import the CSS Module
import { FaArrowRight, FaArrowLeft, FaCloudUploadAlt, FaTrash, FaShareAlt, FaEdit, FaSquare, FaCircle } from 'react-icons/fa';

const formatPresets = {
  story: { width: 340, height: 604, label: 'Story (9:16)', icon: <div className={`${styles.formatIcon} ${styles.story}`} /> },
  portrait: { width: 340, height: 425, label: 'Portrait (4:5)', icon: <div className={`${styles.formatIcon} ${styles.portrait}`} /> },
  square: { width: 340, height: 340, label: 'Square (1:1)', icon: <div className={`${styles.formatIcon} ${styles.square}`} /> },
};

const ImageUpload = () => {
  const navigate = useNavigate(); 
  
  const [step, setStep] = useState(1);
  const [galleries, setGalleries] = useState([]);
  const [title, setTitle] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [format, setFormat] = useState('portrait');
  const [borderRadius, setBorderRadius] = useState('2%');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(textdb, 'galleries'));
      const fetchedGalleries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGalleries(fetchedGalleries);
    } catch (error) {
      toast.error('Failed to fetch campaigns.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleCreateCampaign = async () => {
    if (!imageFile || !title) {
      toast.error('Please provide a title and a background image.');
      return;
    }
    setIsUploading(true);
    toast.info('Creating campaign...', { autoClose: false, toastId: 'creating' });

    try {
      const imageRef = ref(storage, `gallery_images/${Date.now()}_${imageFile.name}`);
      await uploadBytes(imageRef, imageFile);
      const imageUrl = await getDownloadURL(imageRef);
      
      const newGalleryRef = doc(collection(textdb, 'galleries'));
      const newCampaignId = newGalleryRef.id;

      const galleryData = {
        Title1: title,
        imageUrl,
        fontFamily: 'Poppins', 
        fontColor: '#FFFFFF',
        fontWeight: 'bold',
        borderRadius,
        imageWidth: formatPresets[format].width,
        imageHeight: formatPresets[format].height,
      };
      await setDoc(newGalleryRef, galleryData);

      const positionData = {
        imagePosition: { x: 20, y: formatPresets[format].height - 150 },
        textPosition: { x: 140, y: formatPresets[format].height - 100 },
        imageSize: { width: 100, height: 100 },
        textSize: 24,
      };
      await setDoc(doc(textdb, 'positions', newCampaignId), positionData);
      
      toast.dismiss('creating');
      toast.success('Campaign created! Redirecting to the editor...');

      setTimeout(() => {
        navigate(`/edit/${newCampaignId}`);
      }, 1500);

    } catch (error) {
      console.error("Creation failed:", error);
      toast.dismiss('creating');
      toast.error('Creation failed. Please try again.');
    } finally {
        setIsUploading(false);
    }
  };
  
  const handleDelete = async (galleryId, imageUrl) => {
    if (!window.confirm("Are you sure you want to delete this campaign? This action cannot be undone.")) return;
    try {
        await deleteDoc(doc(textdb, 'galleries', galleryId));
        await deleteDoc(doc(textdb, 'positions', galleryId));
        await deleteObject(ref(storage, imageUrl));
        toast.success('Campaign deleted successfully!');
        setGalleries(galleries.filter(g => g.id !== galleryId));
    } catch (error) {
        toast.error('Failed to delete campaign.');
    }
  };
  
  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <ToastContainer position="bottom-right" theme="colored" />
        <div className={styles.wizardCard}>
          <div className={styles.wizardHeader}>
            <h2>Create New Campaign</h2>
            <div className={styles.stepIndicator}>
              <span className={step >= 1 ? styles.active : ''}>1. Details</span>
              <span className={step >= 2 ? styles.active : ''}>2. Format</span>
            </div>
          </div>
          <div className={styles.wizardContent}>
            {step === 1 && (
                <div className={styles.formGroup}>
                    <label>Campaign Title</label>
                    <input type="text" className={styles.textInput} placeholder="e.g., Summer Festival 2024" value={title} onChange={(e) => setTitle(e.target.value)} />
                    <br/><br/>
                    <label>Campaign Background Image</label>
                    <label htmlFor="image-upload-input" className={styles.imageUploadLabel}>
                        <div className={styles.uploadPlaceholder}>
                          <FaCloudUploadAlt />
                          <span>Click to select image</span>
                        </div>
                        {imagePreview && <img src={imagePreview} alt="Preview" className={styles.imagePreview} />}
                    </label>
                    <input id="image-upload-input" type="file" className={styles.fileInput} onChange={handleImageChange} accept="image/*" />
                </div>
            )}
            {step === 2 && (
                <>
                    <div className={styles.formGroup}>
                        <label>Post Format</label>
                        <div className={styles.cardSelectGroup}>
                            {Object.keys(formatPresets).map(key => (
                                <div key={key} className={`${styles.cardSelect} ${format === key ? styles.selected : ''}`} onClick={() => setFormat(key)}>
                                    {formatPresets[key].icon}
                                    <span>{formatPresets[key].label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label>User Photo Shape</label>
                        <div className={styles.cardSelectGroup}>
                            <div className={`${styles.cardSelect} ${borderRadius === '2%' ? styles.selected : ''}`} onClick={() => setBorderRadius('2%')}>
                                <div className={styles.iconContainer}><FaSquare /></div>
                                <span>Square</span>
                            </div>
                            <div className={`${styles.cardSelect} ${borderRadius === '50%' ? styles.selected : ''}`} onClick={() => setBorderRadius('50%')}>
                                <div className={styles.iconContainer}><FaCircle /></div>
                                <span>Round</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
          </div>
          <div className={styles.wizardFooter}>
            {step > 1 ? (
              <button className={`${styles.btn} ${styles.secondary}`} onClick={prevStep}><FaArrowLeft /> Back</button>
            ) : <div/> /* Empty div for spacing */}
            
            {step === 1 && <button className={`${styles.btn} ${styles.primary}`} onClick={nextStep} disabled={!title || !imageFile}>Next <FaArrowRight /></button>}
            {step === 2 && <button className={`${styles.btn} ${styles.success}`} onClick={handleCreateCampaign} disabled={isUploading}>{isUploading ? "Creating..." : "Create & Edit Layout"}</button>}
          </div>
        </div>

        <div className={styles.listContainer}>
          <h2>All Campaigns ({galleries.length})</h2>
          {isLoading ? <p>Loading campaigns...</p> : (
            <div className={styles.galleryGrid}>
                {galleries.map(gallery => (
                    <div key={gallery.id} className={styles.galleryCard}>
                        <img src={gallery.imageUrl} alt={gallery.Title1} className={styles.galleryCardImg} />
                        <div className={styles.galleryCardOverlay}>
                            <h3>{gallery.Title1}</h3>
                            <div className={styles.galleryCardActions}>
                                <Link to={`/${gallery.id}`} className={styles.btnIcon} title="Share Page"><FaShareAlt /></Link>
                                <Link to={`/edit/${gallery.id}`} className={styles.btnIcon} title="Edit Campaign"><FaEdit /></Link>
                                <button onClick={() => handleDelete(gallery.id, gallery.imageUrl)} className={`${styles.btnIcon} ${styles.danger}`} title="Delete"><FaTrash /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;